/**
 * ProductGridShowcase Component
 * 3x3 grid display of featured products for hero section
 */

import { Link } from "react-router-dom";
import { Product } from "@/hooks/useProducts";
import { cn } from "@/lib/utils";

interface ProductGridShowcaseProps {
  products: Product[];
  className?: string;
}

const ProductGridShowcase = ({ products, className }: ProductGridShowcaseProps) => {
  // Take first 9 products for the grid
  const gridProducts = products.slice(0, 9);

  // If less than 9 products, fill remaining with placeholders
  const displayProducts = [...gridProducts];
  while (displayProducts.length < 9) {
    displayProducts.push({
      id: `placeholder-${displayProducts.length}`,
      name: "Coming Soon",
      price: 0,
      images: [],
      featured: false,
    } as Product);
  }

  return (
    <div className={cn("grid grid-cols-3 gap-2 md:gap-3", className)}>
      {displayProducts.map((product, index) => {
        const isPlaceholder = product.id.startsWith('placeholder');
        const imageUrl = product.images?.[0]?.url || '/placeholder.svg';

        return (
          <Link
            key={product.id}
            to={isPlaceholder ? "#" : `/product/${product.id}`}
            className={cn(
              "group relative aspect-square rounded-lg overflow-hidden",
              "bg-gradient-to-br from-gray-800 to-gray-900",
              "border border-gray-700/50 hover:border-brand-cyan/50",
              "transition-all duration-300",
              !isPlaceholder && "hover:scale-105 hover:shadow-xl hover:shadow-brand-cyan/20"
            )}
          >
            {/* Product Image */}
            <div className="absolute inset-0">
              <img
                src={imageUrl}
                alt={product.name}
                className={cn(
                  "w-full h-full object-cover",
                  !isPlaceholder && "group-hover:scale-110 transition-transform duration-300"
                )}
              />
              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Product Info Overlay */}
            {!isPlaceholder && (
              <div className="absolute bottom-0 left-0 right-0 p-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-white text-xs font-medium truncate">
                  {product.name}
                </p>
                <p className="text-brand-cyan text-sm font-bold">
                  {product.sale_price ? (
                    <>
                      <span className="line-through text-gray-400 text-xs mr-1">
                        {product.price.toFixed(3)} BD
                      </span>
                      {product.sale_price.toFixed(3)} BD
                    </>
                  ) : (
                    `${product.price.toFixed(3)} BD`
                  )}
                </p>
              </div>
            )}

            {/* Badge */}
            {product.badge && (
              <div className="absolute top-2 right-2 bg-brand-magenta text-white text-xs px-2 py-1 rounded-full font-bold">
                {product.badge}
              </div>
            )}

            {/* Placeholder Text */}
            {isPlaceholder && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-gray-500 text-xs font-medium">
                  Coming Soon
                </span>
              </div>
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default ProductGridShowcase;